import React from "react";
import {
  pathogenicModerateColor,
  pathogenicVeryStrongColor,
  uncertainColor,
} from "../../../../common/helpers/colors";
import "./oncoKB.scss";

export function regexTransformation(txt) {
  const initialRegex = /\(PMID:\s*((?:\d+\s*,\s*)*\d+)\)/g;
  const finalText = txt.replace(initialRegex, (_, pmidNumbers) => {
    const pmidArray = pmidNumbers.split(",").map((id) => id.trim());
    const transformedIds = pmidArray
      .map((id) => `%%PUBMED: ${id}%%`)
      .join(", ");
    return `(${transformedIds})`;
  });

  return finalText;
}

export function oncogenicityIcon(oncogenicity, selectedHeight) {
  if (
    oncogenicity &&
    (oncogenicity.toLowerCase() === "oncogenic" ||
      oncogenicity.toLowerCase() === "likely oncogenic" ||
      oncogenicity.toLowerCase() === "resistance")
  ) {
    return (
      <svg
        viewBox="0 0 100 100"
        style={{ height: selectedHeight, width: "auto" }}
      >
        <circle cx="50" cy="50" r="40" fill="#0968C3" />
        <circle cx="50" cy="50" r="30" fill="white" />
        <circle cx="50" cy="50" r="25" fill="#0968C3" />
        <circle cx="50" cy="50" r="15" fill="white" />
        <circle cx="50" cy="50" r="10" fill="#0968C3" />
      </svg>
    );
  }

  if (oncogenicity && oncogenicity.toLowerCase() === "likely neutral") {
    return (
      <svg
        viewBox="0 0 100 100"
        style={{ height: selectedHeight, width: "auto" }}
      >
        <circle cx="50" cy="50" r="40" fill="#696969" />
        <circle cx="50" cy="50" r="30" fill="white" />
        <circle cx="50" cy="50" r="25" fill="#696969" />
        <circle cx="50" cy="50" r="15" fill="white" />
        <circle cx="50" cy="50" r="10" fill="#696969" />
      </svg>
    );
  }

  if (oncogenicity && oncogenicity.toLowerCase() === "inconclusive") {
    return (
      <svg
        viewBox="0 0 100 100"
        style={{ height: selectedHeight, width: "auto" }}
      >
        <circle cx="50" cy="50" r="40" fill="#aaaaaa" />
        <circle cx="50" cy="50" r="30" fill="white" />
        <circle cx="50" cy="50" r="25" fill="#aaaaaa" />
        <circle cx="50" cy="50" r="15" fill="white" />
        <circle cx="50" cy="50" r="10" fill="#aaaaaa" />
      </svg>
    );
  }

  if (oncogenicity && oncogenicity.toLowerCase() === "vus") {
    return (
      <svg
        viewBox="0 0 100 100"
        style={{ height: selectedHeight, width: "auto" }}
      >
        <circle cx="50" cy="50" r="40" fill="#696969" />
        <circle cx="50" cy="50" r="25" fill="white" />
      </svg>
    );
  }

  if (oncogenicity && oncogenicity.toLowerCase() === "unknown") {
    return (
      <svg
        viewBox="0 0 100 100"
        style={{ height: selectedHeight, width: "auto" }}
      >
        <circle cx="50" cy="50" r="40" fill="#cccccc" />
        <circle cx="50" cy="50" r="25" fill="white" />
      </svg>
    );
  }
  return null;
}

export function numberOfTherapeuticSvg(therapeuticLevel) {
  switch (therapeuticLevel) {
    case "LEVEL_1":
      return "1";
    case "LEVEL_2":
      return "2";
    case "LEVEL_3A":
      return "3A";
    case "LEVEL_3B":
      return "3B";
    case "LEVEL_4":
      return "4";
    case "LEVEL_R1":
      return "R1";
    case "LEVEL_R2":
      return "R2";
    default:
      return null;
  }
}

export function numberOfDiagnosticSvg(diagnosticLevel) {
  switch (diagnosticLevel) {
    case "LEVEL_Dx1":
      return "1";
    case "LEVEL_Dx2":
      return "2";
    case "LEVEL_Dx3":
      return "3";
    default:
      return null;
  }
}

export function numberOfPrognosticSvg(prognosticLevel) {
  switch (prognosticLevel) {
    case "LEVEL_Px1":
      return "1";
    case "LEVEL_Px2":
      return "2";
    case "LEVEL_Px3":
      return "3";
    default:
      return null;
  }
}

export function numberOfFDASvg(fdaLevel) {
  switch (fdaLevel) {
    case "LEVEL_Fda1":
      return "1";
    case "LEVEL_Fda2":
      return "2";
    case "LEVEL_Fda3":
      return "3";
    default:
      return null;
  }
}

export function colorSelectDiagnostic(diagnosticLevel) {
  switch (diagnosticLevel) {
    case "LEVEL_Dx1":
      return "#33a02c";
    case "LEVEL_Dx2":
      return "#1f78b4";
    case "LEVEL_Dx3":
      return "#984ea3";
    default:
      return null;
  }
}

export function colorSelectPrognostic(prognosticLevel) {
  switch (prognosticLevel) {
    case "LEVEL_Px1":
      return "#33a02c";
    case "LEVEL_Px2":
      return "#1f78b4";
    case "LEVEL_Px3":
      return "#984ea3";
    default:
      return null;
  }
}

export function colorSelect(therapeuticLevel) {
  switch (therapeuticLevel) {
    case "LEVEL_1":
      return "#33a02c";
    case "LEVEL_2":
      return "#1f78b4";
    case "LEVEL_3A":
      return "#984ea3";
    case "LEVEL_4":
      return "#424242";
    case "LEVEL_R1":
      return "#ee3424";
    case "LEVEL_R2":
      return "#f79a92";
    default:
      return null;
  }
}

export function therapeuticIcon(therapeuticColor, selectedHeight, number) {
  return (
    <svg
      viewBox="0 0 100 100"
      style={{ height: selectedHeight, width: "auto" }}
    >
      <circle cx="50" cy="50" r="30" fill={therapeuticColor} />
      <text
        x="50"
        y="50"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="white"
        fontWeight="bold"
        className="oncokb-icon-styling"
      >
        {number}
      </text>
    </svg>
  );
}

export function diagnosticIcon(diagnosticColor, selectedHeight, number) {
  return (
    <svg
      viewBox="0 0 100 100"
      style={{ height: selectedHeight, width: "auto" }}
    >
      <rect x="20" y="20" width="60" height="60" fill={diagnosticColor} />
      <text
        x="50"
        y="50"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="white"
        fontWeight="bold"
        className="oncokb-icon-styling"
      >
        {number}
      </text>
    </svg>
  );
}

export function prognosticIcon(prognosticColor, selectedHeight, number) {
  return (
    <svg
      viewBox="0 0 100 100"
      style={{ height: selectedHeight, width: "auto" }}
    >
      <polygon points="50,20 20,80 80,80" fill={prognosticColor} />
      <text
        x="50"
        y="60"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="white"
        fontWeight="bold"
        className="oncokb-icon-styling"
      >
        {number}
      </text>
    </svg>
  );
}

export function fdaIcon(selectedHeight, number) {
  return (
    <svg
      viewBox="0 0 100 100"
      style={{ height: selectedHeight, width: "auto" }}
    >
      <circle
        cx="50"
        cy="50"
        r="30"
        fill="white"
        stroke="black"
        strokeWidth="5"
      />
      <text
        x="50"
        y="50"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="black"
        fontWeight="bold"
        className="oncokb-icon-styling"
      >
        {number}
      </text>
    </svg>
  );
}

export function handleTherapeuticLevels(sensitiveLevel) {
  switch (sensitiveLevel) {
    case "LEVEL_1":
      return (
        <span className="tw-font-bold" style={{ color: "#33a02c" }}>
          Level 1
        </span>
      );
    case "LEVEL_2":
      return (
        <span className="tw-font-bold" style={{ color: "#1f78b4" }}>
          Level 2
        </span>
      );
    case "LEVEL_3A":
      return (
        <span className="tw-font-bold" style={{ color: "#984ea3" }}>
          Level 3A
        </span>
      );
    case "LEVEL_3B":
      return (
        <span className="tw-font-bold" style={{ color: "#b28cbd" }}>
          Level 3B
        </span>
      );
    case "LEVEL_4":
      return (
        <span className="tw-font-bold" style={{ color: "#424242" }}>
          Level 4
        </span>
      );
    case "LEVEL_R1":
      return (
        <span className="tw-font-bold" style={{ color: "#ee3424" }}>
          Level R1
        </span>
      );
    case "LEVEL_R2":
      return (
        <span className="tw-font-bold" style={{ color: "#f79a92" }}>
          Level R2
        </span>
      );
    default:
      return null;
  }
}

export function handleDiagnosticLevels(diagnosticLevel) {
  switch (diagnosticLevel) {
    case "LEVEL_Dx1":
      return (
        <span className="tw-font-bold" style={{ color: "#33a02c" }}>
          Level Dx1
        </span>
      );
    case "LEVEL_Dx2":
      return (
        <span className="tw-font-bold" style={{ color: "#1f78b4" }}>
          Level Dx2
        </span>
      );
    case "LEVEL_Dx3":
      return (
        <span className="tw-font-bold" style={{ color: "#984ea3" }}>
          Level Dx3
        </span>
      );
    default:
      return null;
  }
}

export function handlePrognosticLevels(highestPrognosticImplicationLevel) {
  switch (highestPrognosticImplicationLevel) {
    case "LEVEL_Px1":
      return (
        <span className="tw-font-bold" style={{ color: "#33a02c" }}>
          Level Px1
        </span>
      );
    case "LEVEL_Px2":
      return (
        <span className="tw-font-bold" style={{ color: "#1f78b4" }}>
          Level Px2
        </span>
      );
    case "LEVEL_Px3":
      return (
        <span className="tw-font-bold" style={{ color: "#984ea3" }}>
          Level Px3
        </span>
      );
    default:
      return null;
  }
}

export function handleFDALevels(highestFdaLevel) {
  switch (highestFdaLevel) {
    case "LEVEL_Fda1":
      return <span className="tw-font-bold">FDA Level 1</span>;
    case "LEVEL_Fda2":
      return <span className="tw-font-bold">FDA Level 2</span>;
    case "LEVEL_Fda3":
      return <span className="tw-font-bold">FDA Level 3</span>;
    default:
      return null;
  }
}

export function oncokbTierCalculation(
  therapeuticLevel,
  diagnosticLevel,
  prognosticLevel,
  resistanceLevel,
  fdaLevel
) {
  const tierMappings = {
    therapeutic: {
      LEVEL_1: 1,
      LEVEL_2: 2,
      LEVEL_3A: 2,
      LEVEL_3B: 2,
      LEVEL_4: 2,
    },
    diagnostic: {
      LEVEL_Dx1: 1,
      LEVEL_Dx2: 2,
      LEVEL_Dx3: 2,
    },
    prognostic: {
      LEVEL_Px1: 1,
      LEVEL_Px2: 2,
      LEVEL_Px3: 2,
    },
    resistance: {
      LEVEL_R1: 1,
      LEVEL_R2: 2,
    },
    fda: {
      LEVEL_Fda1: 1,
      LEVEL_Fda2: 1,
      LEVEL_Fda3: 2,
    },
  };

  const DEFAULT_TIER = 3;

  const calculateTier = (level, mapping) =>
    level == null ? DEFAULT_TIER : mapping[level] || DEFAULT_TIER;

  const finalTier = Math.min(
    calculateTier(therapeuticLevel, tierMappings.therapeutic),
    calculateTier(diagnosticLevel, tierMappings.diagnostic),
    calculateTier(prognosticLevel, tierMappings.prognostic),
    calculateTier(resistanceLevel, tierMappings.resistance),
    calculateTier(fdaLevel, tierMappings.fda)
  );

  switch (finalTier) {
    case 1:
      return pathogenicVeryStrongColor;
    case 2:
      return pathogenicModerateColor;
    case 3:
      return uncertainColor;
    default:
      return "#d4d4d8";
  }
}
